/**
 * GlobalProvisionScriptsPreview Component
 * 
 * Displays matching global provision scripts in cluster creation forms.
 * Allows users to see and optionally exclude specific scripts from execution.
 * 
 * Feature: 002-global-provision-script
 * Enhancement: Cluster-level granular script management
 */

import React, { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControlLabel,
    Icon,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Tooltip,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CodeIcon from '@material-ui/icons/Code';
import ComputerIcon from '@material-ui/icons/Computer';
import DnsIcon from '@material-ui/icons/Dns';
import AllInclusiveIcon from '@material-ui/icons/AllInclusive';

import * as GlobalProvisionActions from 'app/store/actions/globalProvision.actions';

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(2),
    },
    cardHeader: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    headerIcon: {
        color: '#2196F3',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    configItem: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    configHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    configInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
    },
    configName: {
        fontWeight: 500,
    },
    scriptsList: {
        padding: 0,
        paddingLeft: theme.spacing(4),
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
    },
    scriptItem: {
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    nodeTypeChip: {
        marginLeft: theme.spacing(1),
    },
    priorityChip: {
        marginLeft: theme.spacing(1),
        fontSize: '0.7rem',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(3),
    },
    summaryChip: {
        marginLeft: theme.spacing(1),
    },
    disabledScript: {
        opacity: 0.5,
        textDecoration: 'line-through',
    },
}));

/**
 * Get icon for node type
 */
const getNodeTypeIcon = (nodeType) => {
    switch (nodeType) {
        case 'master':
            return <DnsIcon fontSize="small" />;
        case 'worker':
            return <ComputerIcon fontSize="small" />;
        case 'all':
            return <AllInclusiveIcon fontSize="small" />;
        default:
            return <CodeIcon fontSize="small" />;
    }
};

/**
 * Get node type label
 */
const getNodeTypeLabel = (nodeType) => {
    switch (nodeType) {
        case 'master':
            return 'Head Node';
        case 'worker':
            return 'Compute Nodes';
        case 'all':
            return 'All Nodes';
        default:
            return nodeType;
    }
};

/**
 * GlobalProvisionScriptsPreview Component
 */
const GlobalProvisionScriptsPreview = ({
    provider,
    osType,
    clusterName,
    clusterId,
    excludedScripts = [],
    onExcludedScriptsChange,
    disabled = false,
    compact = false
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    
    // Redux state
    const preview = useSelector(({ globalProvision }) => globalProvision.preview);
    const { configs, matchedCount, totalScripts, loading } = preview;

    // Local state for expanded configs
    const [expandedConfigs, setExpandedConfigs] = React.useState({});

    // Fetch matching configs when provider or osType changes
    useEffect(() => {
        if (provider) {
            const params = { provider };
            if (osType) {
                params.osType = osType;
            }
            if (clusterName) {
                params.clusterName = clusterName;
            }
            if (clusterId) {
                params.clusterId = clusterId;
            }
            dispatch(GlobalProvisionActions.previewMatchingConfigs(params));
        }
        
        return () => {
            dispatch(GlobalProvisionActions.clearPreviewConfigs());
        };
    }, [dispatch, provider, osType, clusterName, clusterId]);

    // Build a flat list of all scripts with their config info
    const allScripts = useMemo(() => {
        const scripts = [];
        configs.forEach(config => {
            config.scripts.forEach(script => {
                scripts.push({
                    configId: config._id,
                    configName: config.name,
                    configPriority: config.priority,
                    // Stable preset key from fixtures (e.g. heidi-pip-user-install-v1).
                    // Lets the org-default toggle in Settings exclude a preset
                    // by a string that survives across Heidi installs (mongo
                    // ObjectIds differ per install).
                    presetId: config.presetId || null,
                    presetToken: config.presetId ? 'preset:' + config.presetId : null,
                    scriptId: script._id,
                    scriptName: script.name,
                    scriptDescription: script.description,
                    nodeType: script.nodeType,
                    order: script.order,
                    // Unique identifier for checkbox state
                    uniqueId: `${config._id}-${script._id}`
                });
            });
        });
        return scripts;
    }, [configs]);

    // A script is excluded if EITHER its mongo-id pair OR its preset-level
    // token is in excludedScripts. The preset-level form is what the
    // "Disable PIP_USER default" Settings toggle and similar org-defaults
    // write; the mongo-id form is what per-script checkboxes write.
    const isScriptExcluded = useCallback((script) => {
        if (!script) return false;
        if (excludedScripts.includes(script.uniqueId)) return true;
        if (script.presetToken && excludedScripts.includes(script.presetToken)) return true;
        return false;
    }, [excludedScripts]);

    // Toggle script exclusion. When the script is currently excluded via the
    // preset-level token, removing the per-script entry is not enough; we
    // also strip the preset token so the user can override the org default.
    // Adding to excluded uses the per-script uniqueId form.
    const handleScriptToggle = useCallback((script) => {
        if (disabled) return;

        const currentlyExcluded = isScriptExcluded(script);
        let newExcluded;
        if (currentlyExcluded) {
            // Re-enable: drop both forms.
            newExcluded = excludedScripts.filter(
                (id) => id !== script.uniqueId && (!script.presetToken || id !== script.presetToken)
            );
        } else {
            newExcluded = [...excludedScripts, script.uniqueId];
        }

        if (onExcludedScriptsChange) {
            onExcludedScriptsChange(newExcluded);
        }
    }, [disabled, excludedScripts, isScriptExcluded, onExcludedScriptsChange]);

    // Toggle config expansion
    const handleConfigToggle = (configId) => {
        setExpandedConfigs(prev => ({
            ...prev,
            [configId]: !prev[configId]
        }));
    };

    // Calculate enabled script count
    const enabledScriptCount = useMemo(() => {
        return allScripts.filter(s => !isScriptExcluded(s)).length;
    }, [allScripts, isScriptExcluded]);

    // Don't render if no provider selected
    if (!provider) {
        return null;
    }

    // Loading state
    if (loading) {
        return (
            <Card className={classes.root}>
                <CardHeader
                    className={classes.cardHeader}
                    title={
                        <Box className={classes.headerTitle}>
                            <Icon className={classes.headerIcon}>playlist_play</Icon>
                            <Typography variant="subtitle1">Global Provision Scripts</Typography>
                        </Box>
                    }
                />
                <CardContent className={classes.loadingContainer}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                        Loading matching scripts...
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Empty state
    if (matchedCount === 0) {
        return (
            <Card className={classes.root}>
                <CardHeader
                    className={classes.cardHeader}
                    title={
                        <Box className={classes.headerTitle}>
                            <Icon className={classes.headerIcon}>playlist_play</Icon>
                            <Typography variant="subtitle1">Global Provision Scripts</Typography>
                        </Box>
                    }
                />
                <CardContent className={classes.emptyState}>
                    <Icon color="disabled" style={{ fontSize: 40 }}>code_off</Icon>
                    <Typography variant="body2" color="textSecondary">
                        No global provision scripts configured for this provider
                        {osType && ` and OS type (${osType})`}.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={classes.root}>
            <CardHeader
                className={classes.cardHeader}
                title={
                    <Box className={classes.headerTitle}>
                        <Icon className={classes.headerIcon}>playlist_play</Icon>
                        <Typography variant="subtitle1">Global Provision Scripts</Typography>
                        <Chip
                            size="small"
                            label={`${enabledScriptCount}/${totalScripts} scripts enabled`}
                            color={enabledScriptCount === totalScripts ? 'primary' : 'default'}
                            className={classes.summaryChip}
                        />
                    </Box>
                }
                subheader={
                    <Typography variant="caption" color="textSecondary">
                        These scripts will run automatically during cluster provisioning. Uncheck to exclude.
                    </Typography>
                }
            />
            <CardContent style={{ padding: 0 }}>
                {configs.map((config) => {
                    const isExpanded = expandedConfigs[config._id] === true; // Default to collapsed
                    const configScripts = allScripts.filter(s => s.configId === config._id);
                    const enabledInConfig = configScripts.filter(s => !isScriptExcluded(s)).length;
                    
                    return (
                        <div key={config._id} className={classes.configItem}>
                            <div
                                className={classes.configHeader}
                                onClick={() => handleConfigToggle(config._id)}
                            >
                                <Box className={classes.configInfo}>
                                    <IconButton size="small">
                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                    <Typography className={classes.configName}>
                                        {config.name}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`P${config.priority}`}
                                        variant="outlined"
                                        className={classes.priorityChip}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${enabledInConfig}/${config.scriptCount} scripts`}
                                        color={enabledInConfig === config.scriptCount ? 'default' : 'secondary'}
                                        className={classes.summaryChip}
                                    />
                                </Box>
                            </div>
                            
                            <Collapse in={isExpanded}>
                                <List className={classes.scriptsList} dense>
                                    {configScripts.map((script) => {
                                        const isExcluded = isScriptExcluded(script);

                                        return (
                                            <ListItem
                                                key={script.uniqueId}
                                                className={`${classes.scriptItem} ${isExcluded ? classes.disabledScript : ''}`}
                                                button={!disabled}
                                                onClick={() => !disabled && handleScriptToggle(script)}
                                            >
                                                <ListItemIcon style={{ minWidth: 36 }}>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={!isExcluded}
                                                        disabled={disabled}
                                                        tabIndex={-1}
                                                        disableRipple
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                <ListItemIcon style={{ minWidth: 32 }}>
                                                    <Tooltip title={getNodeTypeLabel(script.nodeType)}>
                                                        {getNodeTypeIcon(script.nodeType)}
                                                    </Tooltip>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={script.scriptName}
                                                    secondary={script.scriptDescription}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        style: isExcluded ? { textDecoration: 'line-through' } : {}
                                                    }}
                                                    secondaryTypographyProps={{
                                                        variant: 'caption'
                                                    }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <Chip
                                                        size="small"
                                                        label={getNodeTypeLabel(script.nodeType)}
                                                        variant="outlined"
                                                        icon={getNodeTypeIcon(script.nodeType)}
                                                        className={classes.nodeTypeChip}
                                                    />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};

GlobalProvisionScriptsPreview.propTypes = {
    /** Cloud provider code (aws, gcp, azure, oracle, maas, ibm) */
    provider: PropTypes.string,
    /** OS type for filtering (e.g., 'ubuntu-22.04') */
    osType: PropTypes.string,
    /** Cluster name for SPECIFIC_CLUSTERS matching */
    clusterName: PropTypes.string,
    /** Array of excluded script unique IDs (format: configId-scriptId) */
    excludedScripts: PropTypes.arrayOf(PropTypes.string),
    /** Callback when excluded scripts change */
    onExcludedScriptsChange: PropTypes.func,
    /** Whether the component is disabled (view-only mode) */
    disabled: PropTypes.bool,
    /** Compact mode for smaller spaces */
    compact: PropTypes.bool
};

export default GlobalProvisionScriptsPreview;
